import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ZoomTransformation } from '../../../common/zoom/models/zoom-model';
import { CirclePacking } from '../../models/circlePackingModel';
import { FONT_SIZE_VALUE } from '../../render-utils/constants';

@Component({
  selector: '[chart-circle-packing-ui]',
  standalone: true,
  imports: [CommonModule],
  template: `
    <!--

     STATES for circle-ui
     * has-children: If the circle has hierarchical data associated as children
     * selected: if the circle has been selected via checkbox
     * active-depth: if the level of zoom is the same as the circle depth
     * inactive-level: when a user clicks in a circle zooms in the circle
        and sees its children all the other circles and their children should be hidden

    -->
    <svg:g
      class="circle-ui"
      [ngClass]="{
        'has-children': circle.children,
        selected: selectedMap && selectedMap[circle[modelProperty]],
        'active-depth': actualDepth === circle.depth,
        'inactive-circle': isInactive
      }"
      (mouseenter)="onMouseEnter(circle)"
      (mouseleave)="onMouseLeave()"
    >
      <!-- RING -->

      <svg:circle
        class="circle-ui-ring"
        [attr.fill]="activeCircle ? (circle[modelProperty] === activeCircle[modelProperty] ? 'white' : circle.color) : circle.color"
        [attr.fill-opacity]="activeCircle ? (circle[modelProperty] === activeCircle[modelProperty] ? 0 : 0.1) : 0.1"
        (click)="onZoomIn(circle, $event)"
        [attr.cx]="circle.x"
        [attr.cy]="circle.y"
        [attr.r]="circle.r"
      />

      <!-- CHECKBOX -->

      <svg:g
        *ngIf="zoom"
        class="circle-ui-checkbox"
        [attr.transform]="'translate(' + (circle.x - HALF_SIZE / zoom.k) + ',' + (circle.y - TEXT_HEIGHT - EXTRA_SIZE / zoom.k) + ')'"
        (click)="onSelect(circle, $event)"
      >
        <svg:rect
          class="circle-ui-checkbox-box-padding"
          x="0"
          y="0"
          [attr.y]="-(HALF_SIZE / zoom.k)"
          [attr.x]="-(HALF_SIZE / zoom.k)"
          [attr.width]="DOUBLE_SIZE / zoom.k"
          [attr.height]="DOUBLE_SIZE / zoom.k"
        />
        <svg:rect
          class="circle-ui-checkbox-box"
          x="0"
          y="0"
          [attr.rx]="BORDER_RADIUS / zoom.k"
          [attr.stroke]="circle.color"
          [attr.stroke-width]="STROKE_WIDTH_CHECKBOX / zoom.k"
          [attr.width]="SIZE / zoom.k"
          [attr.height]="SIZE / zoom.k"
        />

        <svg:polygon
          *ngIf="selectedMap && selectedMap[circle[modelProperty]]"
          [attr.transform]="
            'translate(' +
            CHECKBOX_X_OFFSET * (CHECKBOX_SCALE / zoom.k) +
            ', ' +
            CHECKBOX_Y_OFFSET * (CHECKBOX_SCALE / zoom.k) +
            ') scale(' +
            CHECKBOX_SCALE / zoom.k +
            ')'
          "
          [attr.fill]="circle.color"
          points="2.5302734,5.5908203 0,3.0605469 1.0605469,2 2.5302734,3.4697266 6,0 7.0605469,1.0605469 "
        />
      </svg:g>

      <!-- REMOVE BUTTON -->
      <svg:g
        *ngIf="zoom"
        class="circle-ui-remove-btn"
        [class.show]="showRemoveBtn"
        (click)="onRemove(circle, $event)"
        [attr.transform]="
          'translate(' +
          (circle.x - (REMOVE_BTN_SIZE * REMOVE_BTN_SCALE) / 2 / zoom.k) +
          ',' +
          (circle.y + circle.r - (REMOVE_BTN_SIZE * REMOVE_BTN_SCALE) / zoom.k) +
          ') scale(' +
          REMOVE_BTN_SCALE / zoom.k +
          ') translate(' +
          0 +
          ',' +
          -REMOVE_BTN_MARGIN * circle.r +
          ')'
        "
      >
        <svg:circle
          class="circle-ui-remove-btn-circle"
          [attr.cx]="REMOVE_BTN_SIZE / 2"
          [attr.cy]="REMOVE_BTN_SIZE / 2"
          [attr.r]="REMOVE_BTN_SIZE / 2"
        />

        <svg:g
          [attr.transform]="
            'translate(' + (REMOVE_BTN_SIZE / 2 - REMOVE_BTN_ICON_SIZE / 2) + ', ' + (REMOVE_BTN_SIZE / 2 - REMOVE_BTN_ICON_SIZE / 2) + ')'
          "
        >
          <svg:path
            class="circle-ui-remove-btn-icon"
            d="M3.4,4.8L0.3,1.7c-0.4-0.4-0.4-1,0-1.4l0,0c0.4-0.4,1-0.4,1.4,0l3.1,3.1l3.1-3.1c0.4-0.4,1-0.4,1.4,0l0,0c0.4,0.4,0.4,1,0,1.4L6.2,4.8l3.1,3.1c0.4,0.4,0.4,1,0,1.4l0,0c-0.4,0.4-1,0.4-1.4,0L4.8,6.2L1.7,9.3c-0.4,0.4-1,0.4-1.4,0l0,0c-0.4-0.4-0.4-1,0-1.4L3.4,4.8z"
          />
        </svg:g>
      </svg:g>
    </svg:g>

    <!-- RENDER CHILDREN RECURSIVELY -->
    <ng-container *ngIf="circle.children">
      <ng-container *ngFor="let child of circle.children">
        <svg:g
          chart-circle-packing-ui
          [circle]="child"
          [modelProperty]="modelProperty"
          [active]="activeCircle"
          [selectedMap]="selectedMap"
          [actualDepth]="actualDepth"
          [zoom]="zoom"
          (zoomIn)="zoomIn.emit($event)"
          (select)="select.emit($event)"
          (hover)="hover.emit($event)"
          (remove)="remove.emit($event)"
        ></svg:g>
      </ng-container>
    </ng-container>
  `,
  styleUrls: ['./circle-packing-ui.component.scss']
})
export class CirclePackingUiComponent implements OnInit {
  @Input()
  circle: CirclePacking;

  @Input()
  public modelProperty: string;

  //  Active is the circle where the user currently is zoomed in
  public activeCircle: CirclePacking;
  @Input()
  set active(active: CirclePacking) {
    this.activeCircle = active;

    if (active) {
      this.isInactive =
        !this.circle.ancestors.find((d) => d[this.modelProperty] === active[this.modelProperty]) &&
        this.circle[this.modelProperty] !== active[this.modelProperty];
    } else {
      this.isInactive = false;
    }
  }

  @Input()
  selectedMap: CirclePacking;

  @Input()
  actualDepth: number;

  @Input()
  zoom: ZoomTransformation;

  /* eslint-disable @angular-eslint/no-output-native */
  @Output()
  public select: EventEmitter<CirclePacking> = new EventEmitter();
  /* eslint-enable @angular-eslint/no-output-native */

  @Output()
  public zoomIn: EventEmitter<CirclePacking> = new EventEmitter();

  @Output()
  public hover: EventEmitter<CirclePacking> = new EventEmitter();

  @Output()
  public remove: EventEmitter<CirclePacking> = new EventEmitter();

  // CONSTANTS
  public TEXT_HEIGHT: number;
  public VALUE_HEIGHT: number;

  public SIZE = 14;
  public EXTRA_SIZE = this.SIZE * 1.3;
  public DOUBLE_SIZE = this.SIZE * 2;
  public HALF_SIZE = this.SIZE / 2;
  public STROKE_WIDTH_RING = 2;
  public STROKE_WIDTH_CHECKBOX = 1;
  public BORDER_RADIUS = 2;

  // Checkbox
  public CHECKBOX_SCALE = this.SIZE / 10;
  public CHECKBOX_X_OFFSET = 1.5;
  public CHECKBOX_Y_OFFSET = 2;

  // Remove button
  public REMOVE_BTN_SIZE = 20;
  public REMOVE_BTN_SCALE = 1.3;
  public REMOVE_BTN_ICON_SIZE = 10;
  public REMOVE_BTN_MARGIN = 0.1;
  public showRemoveBtn: boolean;

  public isInactive: boolean;

  // we want to delay a little the hover response
  // so when the user hovers the "X" button does not jump so frenetically
  private removeBtnHoverTimeout: ReturnType<typeof setTimeout>;
  private REMOVE_BTN_HOVER_DELAY = 150;

  ngOnInit() {
    this.TEXT_HEIGHT = (this.circle.wrappedText.lines.length * this.circle.wrappedText.fontSize) / 2;

    this.VALUE_HEIGHT = (this.circle.wrappedText.fontSize * FONT_SIZE_VALUE) / 4;
  }

  public onSelect(circle: CirclePacking, event: MouseEvent) {
    event.stopPropagation();
    this.select.emit(circle);
  }

  public onRemove(circle: CirclePacking, event: MouseEvent) {
    event.stopPropagation();
    this.remove.emit(circle);
  }

  public onZoomIn(circle: CirclePacking, event: MouseEvent) {
    event.stopPropagation();
    this.zoomIn.emit(circle);
  }

  public onMouseEnter(circle: CirclePacking) {
    this.removeBtnHoverTimeout = setTimeout(() => {
      this.showRemoveBtn = true;
      this.hover.emit(circle);
    }, this.REMOVE_BTN_HOVER_DELAY);
  }

  public onMouseLeave() {
    clearTimeout(this.removeBtnHoverTimeout);
    this.showRemoveBtn = false;
    this.hover.emit(null);
  }
}
